import { ChevronUpDownIcon } from "@heroicons/react/24/outline";
import { PencilIcon } from "@heroicons/react/24/solid";
import React from "react";
import discussion from "assets/discussion.svg";
import {
  Card,
  CardHeader,
  Typography,
  Button,
  CardBody,
  Chip,
  CardFooter,
  Tabs,
  TabsHeader,
  Tab,
  Avatar,
} from "@material-tailwind/react";
import { FiSearch } from "react-icons/fi";
import { useEffect, useState } from "react";
import { ExerciseStatus, SubmissionType } from "types";
import { TableRow, fetchExercises } from "./StudentSubmissionUtils";
import ConsultModal from "./DetailsModal";
import { EditModal } from "./UpdateModal";
import { GetSubmissionsOutput } from "server/StartupAdmins CRUD/OutputsTypes";
import { BASE_URL } from "server";

const TABS = [
  {
    label: "Tout",
    value: "",
  },
  {
    label: "Contrôles",
    value: SubmissionType.CONTROL,
  },
  {
    label: "Blocages",
    value: SubmissionType.BLOCAGE,
  },
  {
    label: "Mal compréhensions",
    value: SubmissionType.UNCLEAR,
  },
];

const TABLE_HEAD = ["Soumission", "Statut", "Mise à jour", "Observation", ""];

const statusOptions = [
  { value: "", label: "Statut" },
  { value: ExerciseStatus.STANDBY, label: "En attente" },
  { value: ExerciseStatus.PROCESSING, label: "En cours" },
  { value: ExerciseStatus.ANSWERED, label: "Résolue" },
  { value: ExerciseStatus.REJECTED, label: "Rejetée" },
];

const saveToSessionStorage = (key: string, data: any) => {
  sessionStorage.setItem(key, JSON.stringify(data));
};

const retrieveFromSessionStorage = (key: string): any => {
  const data = sessionStorage.getItem(key);
  return data ? JSON.parse(data) : null;
};

export default function SubmissionList2() {
  const [activeTab, setActiveTab] = useState<string>("");
  const [filteredStatus, setFilteredStatus] = useState("");
  const [filteredRows, setFilteredRows] = useState<TableRow[]>([]);
  const [fullSubmissionsData, setFullSubmissionsData] = useState<
    GetSubmissionsOutput[]
  >([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [consultModalOpen, setConsultModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSubmissionId, setEditingSubmissionId] = useState<string>("");
  const [selectedSubmission, setSelectedSubmission] =
    useState<GetSubmissionsOutput | null>(null);

  useEffect(() => {
    const storedRows = retrieveFromSessionStorage("TABLE_ROWS");
    if (storedRows) {
      setFilteredRows(storedRows);
    }
  }, []);

  useEffect(() => {
    saveToSessionStorage("TABLE_ROWS", filteredRows);
  }, [filteredRows]);

  useEffect(() => {
    const fetchData = async () => {
      const newRows = await fetchExercises();
      setFilteredRows(newRows);

      // Récupérer les données complètes depuis l'API
      try {
        const response = await fetch(`${BASE_URL}/problems/mine`, {
          method: "GET",
          headers: {
            Accept: "*/*",
          },
          credentials: "include",
        });

        if (response.ok) {
          const fullData = await response.json();
          console.log("Données complètes récupérées:", fullData);
          setFullSubmissionsData(fullData);
        } else {
          console.error("Erreur API:", response.status, response.statusText);
        }
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des données complètes:",
          error,
        );
      }
    };

    fetchData();
    const interval = setInterval(() => {
      fetchData();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const filteredRowsData = filteredRows.filter((row) => {
    const tabMatch = activeTab === "" || row.submissionType === activeTab;
    const statusMatch = filteredStatus === "" || row.status === filteredStatus;
    const searchMatch =
      searchQuery === "" ||
      row.title.toLowerCase().includes(searchQuery.toLowerCase());
    return tabMatch && statusMatch && searchMatch;
  });

  const handleConsult = (submission: TableRow) => {
    console.log("handleConsult appelé avec:", submission);
    console.log("fullSubmissionsData disponibles:", fullSubmissionsData);

    // Trouver les données complètes correspondantes
    const fullSubmissionData = fullSubmissionsData.find(
      (fullData) => fullData._id === submission._id,
    );

    console.log("Données complètes trouvées:", fullSubmissionData);

    if (fullSubmissionData) {
      setSelectedSubmission(fullSubmissionData);
    } else {
      // Fallback: créer un objet avec les données disponibles
      const fallbackData = {
        _id: submission._id,
        matiere: "Matière non disponible",
        submissionType: submission.submissionType,
        referenceType: "other",
        referenceBook: undefined,
        referenceTest: undefined,
        referenceOther: { info: "Information non disponible" },
        productionFileIds: [],
        subjectFileIds: [],
        studentId: "",
        comment: "",
        idConversation: undefined,
        isActivated: submission.isActivated,
        status: submission.status,
        correctorId: undefined,
        supervisorId: undefined,
        createdAt: "",
        updatedAt: submission.timestamp,
        __v: 0,
        observation: submission.observation,
        rejectedBy: undefined,
        student: undefined,
      } as unknown as GetSubmissionsOutput;

      console.log("Utilisation des données de fallback:", fallbackData);
      setSelectedSubmission(fallbackData);
    }

    setConsultModalOpen(true);
  };

  const handleEdit = (submission: TableRow) => {
    setEditingSubmissionId(submission._id);
    setEditModalOpen(true);
  };

  const handleSaveEdit = (updatedDetails: {
    title: string;
    observation: string;
  }) => {
    // Mettre à jour les données ici
    setEditModalOpen(false);
  };
  const getStatusLabel = (status: string): string => {
    const statusOption = statusOptions.find(
      (option) => option.value === status,
    );
    return statusOption ? statusOption.label : status; // Retourne le status original si non trouvé
  };

  function handleSave(updatedDetails: { comment?: string | undefined }): void {
    // Logique de sauvegarde du commentaire
    // Ici vous pouvez ajouter la logique pour sauvegarder le commentaire
    // Par exemple, appeler une API ou mettre à jour l'état local
  }

  return (
    <>
      <Card className="h-full w-full bg-sidemenubg dark:bg-boxdark">
        <CardHeader
          floated={false}
          shadow={false}
          className="rounded-none bg-sidemenubg dark:bg-boxdark"
        >
          <div className="mb-8 flex items-center justify-between gap-8">
            <div>
              <Typography variant="h5" color="blue-gray">
                Mes soumissions
              </Typography>
              <Typography color="gray" className="mt-1 font-normal">
                Voir et suivre toutes vos soumissions.
              </Typography>
            </div>
          </div>
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            {/* Desktop: Tabs complets */}
            <div className="hidden lg:block">
              <Tabs value={activeTab} className="w-max bg-white rounded-lg">
                <TabsHeader>
                  {TABS.map(({ label, value }) => (
                    <Tab
                      key={value}
                      value={value}
                      onClick={() => setActiveTab(value)}
                      className={`whitespace-nowrap ${
                        activeTab === value
                          ? "text-[#131CC9] border-b-2 border-[#E3B505]"
                          : ""
                      }`}
                    >
                      {label}
                    </Tab>
                  ))}
                </TabsHeader>
              </Tabs>
            </div>

            {/* Mobile: Tabs contractés */}
            <div className="lg:hidden w-full">
              <Tabs value={activeTab} className="w-full bg-white rounded-lg">
                <TabsHeader className="flex flex-nowrap overflow-x-auto min-h-0">
                  {TABS.map(({ label, value }) => {
                    // Versions contractées pour mobile
                    const mobileLabel =
                      {
                        Tout: "Tout",
                        Contrôles: "Contrôles",
                        Blocages: "Blocages",
                        "Mal compréhensions": "Mal comp.",
                      }[label] || label;

                    return (
                      <Tab
                        key={value}
                        value={value}
                        onClick={() => setActiveTab(value)}
                        className={`whitespace-nowrap flex-shrink-0 px-1 py-0 text-xs min-h-0 h-6 ${
                          activeTab === value
                            ? "text-[#131CC9] border-b-2 border-[#E3B505]"
                            : ""
                        }`}
                      >
                        {mobileLabel}
                      </Tab>
                    );
                  })}
                </TabsHeader>
              </Tabs>
            </div>
            <div className="w-full lg:w-72 sm:mr-5">
              <div className="relative">
                <button className="absolute top-1/2 right-0 -translate-y-1/2 bg-[#E3B505] shadow-xs rounded-full hover:bg-white text-gray-800 font-bold py-1.5 px-1.5 rounded">
                  <FiSearch size={20} className="lg:w-6 lg:h-6" />
                </button>
                <input
                  type="text"
                  placeholder="Rechercher..."
                  className="w-full pr-12 lg:pr-4 pl-3 lg:pl-9 focus:outline-none rounded-full shadow-sm border border-gray-200 text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardBody className="overflow-scroll px-0">
          <table className="mt-4 w-full min-w-max table-auto text-left">
            <thead>
              <tr>
                {TABLE_HEAD.map((head, index) => (
                  <th
                    key={head}
                    className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-4 transition-colors hover:bg-blue-gray-50"
                  >
                    {head === "Statut" ? (
                      <div className="flex flex-row items-center justify-center w-full space-x-8">
                        <select
                          className="w-full rounded  bg-transparent  border-transparent focus:outline-none focus:ring-0 focus:border-transparent"
                          value={filteredStatus}
                          onChange={(e) => {
                            setFilteredStatus(e.target.value);
                          }}
                        >
                          {statusOptions.map(({ label, value }, index) => {
                            return (
                              <option key={index} value={value}>
                                {label}
                              </option>
                            );
                          })}
                        </select>
                        {index !== TABLE_HEAD.length - 1 && (
                          <ChevronUpDownIcon
                            strokeWidth={2}
                            className="h-4 w-4"
                          />
                        )}
                      </div>
                    ) : (
                      <Typography
                        variant="small"
                        color="blue-gray"
                        className="flex items-center justify-between gap-2 font-normal leading-none opacity-70"
                      >
                        {head}{" "}
                        {index !== TABLE_HEAD.length - 1 && (
                          <ChevronUpDownIcon
                            strokeWidth={2}
                            className="h-4 w-4"
                          />
                        )}
                      </Typography>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRowsData.map(
                (
                  {
                    _id,
                    title,
                    status,
                    updatedAt,
                    observation,
                    submissionType,
                    isActivated,
                  },
                  index,
                ) => {
                  const isLast = index === filteredRowsData.length - 1;
                  const classes = isLast
                    ? "p-4 bg-white"
                    : "p-4 border-b border-blue-gray-50 bg-white";

                  return (
                    <tr key={_id}>
                      <td className={classes}>
                        <div className="flex items-center gap-3">
                          <Avatar src={discussion} alt={title} size="sm" />
                          <div className="flex flex-col">
                            <Typography
                              variant="small"
                              color="blue-gray"
                              className="font-normal"
                            >
                              {title}
                            </Typography>
                          </div>
                        </div>
                      </td>
                      <td className={classes}>
                        <div className="w-max bg-blue-200 rounded rounded-lg">
                          <Chip
                            variant="ghost"
                            size="sm"
                            value={getStatusLabel(status)}
                            color={
                              status === ExerciseStatus.STANDBY
                                ? "light-blue"
                                : status === ExerciseStatus.PROCESSING
                                ? "amber"
                                : status === ExerciseStatus.ANSWERED
                                ? "light-green"
                                : "red"
                            }
                          />
                        </div>
                      </td>
                      <td className={classes}>
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal"
                        >
                          {updatedAt}
                        </Typography>
                      </td>
                      <td className={classes}>
                        <Typography
                          variant="small"
                          color="blue-gray"
                          className="font-normal"
                        >
                          {observation}
                        </Typography>
                      </td>
                      <td className={classes}>
                        {status === ExerciseStatus.STANDBY && !isActivated ? (
                          <Button
                            variant="text"
                            className="flex flex-row space-x-2 bg-[#EDEDED] text-[#131CC9]"
                            onClick={() =>
                              handleEdit({
                                _id,
                                title,
                                status,
                                updatedAt,
                                observation,
                                submissionType,
                                isActivated,
                                timestamp: "",
                              })
                            }
                          >
                            <div>Modifier</div>
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            onClick={() =>
                              handleConsult({
                                _id,
                                title,
                                status,
                                updatedAt,
                                observation,
                                submissionType,
                                isActivated,
                                timestamp: "",
                              })
                            }
                            className="bg-[#EDEDED] text-[#131CC9]"
                          >
                            CONSULTER
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                },
              )}
            </tbody>
          </table>
        </CardBody>
        <CardFooter className="flex items-center justify-between border-t border-blue-gray-50 p-4">
          <div className="flex gap-2"></div>
        </CardFooter>
      </Card>

      {consultModalOpen && (
        <ConsultModal
          open={consultModalOpen}
          onClose={() => setConsultModalOpen(false)}
          submission={selectedSubmission}
        />
      )}

      {editModalOpen && (
        <EditModal
          open={editModalOpen}
          handleOpen={() => setEditModalOpen(false)}
          onSave={handleSave}
          submissionId={editingSubmissionId}
        />
      )}
    </>
  );
}
